import React from 'react';

// ── Progress bar ──────────────────────────────────────────────
interface ProgressProps {
  value: number;
  max?:  number;
  color?: string;
}
export const Progress: React.FC<ProgressProps> = ({ value, max = 100, color }) => (
  <div className="prog">
    <div
      className="fill"
      style={{
        width: `${Math.min(100, (value / max) * 100)}%`,
        ...(color ? { background: color } : {}),
      }}
    />
  </div>
);

// ── Donut ─────────────────────────────────────────────────────
interface DonutProps {
  value:  number;
  label?: string;
  size?:  number;
  stroke?: number;
  color?: string;
}
export const Donut: React.FC<DonutProps> = ({ value, label = 'Total', size = 140, stroke = 14, color = 'var(--primary-600)' }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--border)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
                strokeDasharray={`${dash} ${c}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <b className="block text-[24px] font-extrabold text-ink-900 leading-none">{value}%</b>
          <span className="text-[10px] text-ink-500 uppercase tracking-wider">{label}</span>
        </div>
      </div>
    </div>
  );
};

// ── Bars (CSS only) ───────────────────────────────────────────
interface BarsProps {
  data: { label: string; value: number }[];
  max?: number;
}
export const Bars: React.FC<BarsProps> = ({ data, max }) => {
  const m = max || Math.max(...data.map(d => d.value)) || 1;
  return (
    <div className="bars">
      {data.map((d, i) => (
        <div className="bar" key={i}>
          <div className="stack">
            <div className="fill" style={{ height: `${(d.value / m) * 100}%` }} title={`${d.label}: ${d.value}`} />
          </div>
          <div className="lbl">{d.label}</div>
        </div>
      ))}
    </div>
  );
};

// ── Spark (sparkline) ─────────────────────────────────────────
interface SparkProps {
  data:  number[];
  color?: string;
  fill?:  boolean;
}
export const Spark: React.FC<SparkProps> = ({ data, color = 'var(--primary-600)', fill = true }) => {
  const w = 200, h = 40;
  const max = Math.max(...data), min = Math.min(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 6) - 3}`)
    .join(' ');
  return (
    <svg className="w-full h-10" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {fill && <polygon points={`0,${h} ${pts} ${w},${h}`} fill={color} opacity={0.15} />}
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} />
    </svg>
  );
};
